import React from "react";
import { useTranslation } from "react-i18next";
import { BRAND, buildWhatsAppUrl } from "@/lib/brand";
import { Instagram, MessageCircle, Mail } from "lucide-react";

// Official WhatsApp icon (simplified)
const WhatsApp = () => (
    <svg width="18" height="18" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.066 0-.115-.014-.157-.028-2.085-1.026-3.96-2.937-4.987-5.026-.014-.038-.025-.082-.025-.122 0-.41 1.387-1.131 1.387-1.508 0-.067-.018-.122-.032-.165-.131-.452-.892-2.357-1.067-2.79-.144-.36-.247-.502-.652-.502-.062 0-.182.018-.205.018-.295.041-.715.131-.999.275-.736.348-1.476 1.426-1.476 2.836 0 1.388.652 2.802 1.62 4.06 1.876 2.36 3.79 4.08 6.595 5.183.853.336 1.524.535 2.041.535 1.146 0 2.563-.745 2.99-1.642.135-.275.275-.81.275-1.32 0-.4-.117-.434-.39-.555-.46-.222-2.045-.985-2.4-1.124z"/>
        <path d="M16.001 0C7.165 0 .003 7.163.003 16c0 2.812.749 5.49 2.094 7.834L0 32l8.36-2.18A15.939 15.939 0 0 0 16.001 32C24.838 32 32 24.837 32 16S24.838 0 16.001 0zm0 28.928c-2.467 0-4.846-.69-6.93-1.995l-.5-.297-5.165 1.347 1.377-4.99-.327-.515A12.83 12.83 0 0 1 3.073 16C3.073 8.875 8.876 3.072 16 3.072c7.125 0 12.927 5.803 12.927 12.928 0 7.124-5.802 12.928-12.926 12.928z"/>
    </svg>
);

export const Footer = () => {
    const { t } = useTranslation();
    const year = new Date().getFullYear();
    return (
        <footer
            data-testid="site-footer"
            className="bg-stone-900 text-stone-50 py-20 md:py-24 relative overflow-hidden"
        >
            <div className="absolute inset-0 bg-grain opacity-20" />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4">
                            <img
                                src={BRAND.logo}
                                alt={BRAND.name}
                                className="h-24 w-24 object-contain"
                            />
                            <div>
                                <div className="font-serif text-2xl">{BRAND.name}</div>
                                <div className="text-xs uppercase tracking-[0.24em] text-stone-400 mt-1">
                                    {BRAND.tagline}
                                </div>
                            </div>
                        </div>
                        <p className="mt-7 text-stone-300/90 font-light max-w-md leading-relaxed">
                            {t("footer.tagline_text")}
                        </p>
                    </div>

                    <div className="lg:col-span-3">
                        <div className="text-xs uppercase tracking-[0.24em] text-stone-400 mb-5">
                            {t("footer.contact")}
                        </div>
                        <ul className="space-y-3 text-stone-200">
                            <li className="flex items-center gap-3">
                                <MessageCircle size={16} className="text-gold-soft" />
                                <a
                                    href={buildWhatsAppUrl(t("footer.wa_msg_default"))}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="link-underline"
                                    data-testid="footer-whatsapp"
                                >
                                    {BRAND.whatsappDisplay}
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-gold-soft" />
                                <a
                                    href={`mailto:${BRAND.email}`}
                                    className="link-underline"
                                    data-testid="footer-email"
                                >
                                    {BRAND.email}
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Instagram size={16} className="text-gold-soft" />
                                <a
                                    href={BRAND.instagramUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="link-underline"
                                    data-testid="footer-instagram"
                                >
                                    {BRAND.instagram}
                                </a>
                            </li>
                        </ul>

                        <a
                            href={buildWhatsAppUrl(t("footer.wa_msg_cta"))}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-whatsapp-btn"
                            className="mt-6 inline-flex items-center gap-2.5 px-5 py-3 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium text-sm shadow-lg transition-all hover:-translate-y-0.5"
                        >
                            <WhatsApp />
                            {t("footer.wa_btn")}
                        </a>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="text-xs uppercase tracking-[0.24em] text-stone-400 mb-5">
                            {t("footer.ready")}
                        </div>
                        <p className="font-serif text-2xl md:text-3xl leading-tight">
                            {t("footer.cta_text")}
                        </p>
                        <a
                            href="#quiz"
                            className="mt-6 inline-flex items-center gap-2 text-gold-soft link-underline"
                            data-testid="footer-cta-link"
                        >
                            {t("footer.cta_link")}
                        </a>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-stone-800 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-stone-400">
                    <div>© {year} {BRAND.name}. {t("footer.rights")}</div>
                    <a href="#" className="link-underline" data-testid="footer-privacy">
                        {t("footer.privacy")}
                    </a>
                </div>
            </div>
        </footer>
    );
};
